import { isEqual, isNumber } from 'lodash';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { formatMoney } from 'utils/utils';

const CommissionsTable = ({ data_for_table, months }) => {
  const columns = React.useMemo(() => {
    const orderedMonths = [...(months ?? [])].sort(
      (a, b) => new Date(a) - new Date(b)
    );

    /* eslint react/prop-types: 0 */
    const monthCols = orderedMonths.map((date, index) => ({
      id: `month-${index}`,
      accessorKey: date,
      header: date,
      enableColumnFilter: false,
      enableResizing: false,
      Footer: ({ table }) => {
        const total = table
          .getRowModel()
          .rows.reduce((s, row) => s + (Number(row.original[date]) || 0), 0);
        return (
          <span
            style={{
              color: total < 0 ? '#E85C53' : '#374151',
              fontWeight: 600
            }}
          >
            {formatMoney(total)}
          </span>
        );
      },
      Cell: ({ row }) => {
        const value = row.original[date];
        return (
          <span
            style={
              isNumber(value)
                ? {
                    color:
                      value === 0
                        ? '#909090'
                        : value < 0
                        ? '#E85C53'
                        : '#5DBB29'
                  }
                : undefined
            }
          >
            {isNumber(value) ? formatMoney(value) : 'N/A'}
          </span>
        );
      }
    }));

    const rowTotalCol = {
      id: 'row_total',
      header: 'Total',
      enableColumnFilter: false,
      enableResizing: false,
      accessorFn: row =>
        orderedMonths.reduce((s, d) => s + (Number(row[d]) || 0), 0),
      Footer: ({ table }) => {
        const grand = table
          .getRowModel()
          .rows.reduce(
            (s, row) =>
              s +
              orderedMonths.reduce(
                (ms, d) => ms + (Number(row.original[d]) || 0),
                0
              ),
            0
          );
        return <span style={{ fontWeight: 700 }}>{formatMoney(grand)}</span>;
      },
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return (
          <span
            style={{
              fontWeight: 600,
              color: value < 0 ? '#E85C53' : '#1f2937'
            }}
          >
            {formatMoney(value)}
          </span>
        );
      }
    };

    return [
      {
        header: 'Carrier',
        accessorKey: 'carriername',
        size: 160,
        enableColumnFilter: false,
        Footer: () => (
          <span style={{ fontWeight: 700, color: '#1f2937' }}>Grand Total</span>
        )
      },
      ...monthCols,
      rowTotalCol
    ];
  }, [months]);

  const table = useMaterialReactTable({
    columns,
    data: data_for_table ?? [],
    enableExpanding: false,
    enableExpandAll: false,
    manualPagination: true,
    enableColumnActions: false,
    enableColumnResizing: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableSorting: false,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: false,
    enableColumnPinning: true,
    enableRowNumbers: false,
    initialState: {
      showColumnFilters: false,
      density: 'compact',
      columnPinning: { left: ['carriername'], right: ['row_total'] }
    },
    muiTablePaperProps: {
      elevation: 0,
      style: { display: 'block', padding: 0, borderRadius: 14, overflow: 'hidden' }
    },
    muiTableContainerProps: {
      style: { maxHeight: '520px', scrollbarWidth: 'thin' }
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        backgroundColor: '#f3f4f6',
        fontWeight: 700,
        fontSize: '0.75rem',
        color: '#374151',
        borderBottom: '2px solid #e5e7eb',
        textAlign: column.id !== 'carriername' ? 'center' : 'left',
        py: 1
      }
    }),
    muiTableFooterCellProps: ({ column }) => ({
      sx: {
        backgroundColor: '#f3f4f6',
        fontWeight: 700,
        fontSize: '0.75rem',
        color: '#374151',
        borderTop: '2px solid #e5e7eb',
        textAlign: column.id !== 'carriername' ? 'right' : 'left'
      }
    }),
    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        fontSize: '0.8rem',
        py: '4px',
        borderBottom: '1px solid #f3f4f6',
        textAlign: column.id !== 'carriername' ? 'right' : 'left'
      }
    }),
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.index % 2 === 0 ? '#ffffff' : '#f9fafb',
        '&:hover td': { backgroundColor: '#eff6ff !important' }
      }
    }),
    renderEmptyRowsFallback: () => (
      <h2
        style={{
          color: 'grey',
          textAlign: 'center',
          fontWeight: '400',
          fontSize: 14,
          padding: 15
        }}
      >
        No Commissions found
      </h2>
    )
  });

  return (
    <div style={{ width: '100%' }}>
      <MaterialReactTable table={table} />
    </div>
  );
};

CommissionsTable.propTypes = {
  data_for_table: PropTypes.array,
  months: PropTypes.array
};

export default memo(CommissionsTable, (prevProps, nextProps) =>
  isEqual(prevProps.data_for_table, nextProps.data_for_table)
);
